import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { usePathname, useRouter } from 'expo-router'
import { useCallback, useState } from 'react'
import { type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form'
import { FlatList, StyleSheet, TouchableOpacity, View } from 'react-native'
import {
    ActivityIndicator,
    Checkbox,
    Dialog,
    HelperText,
    Icon,
    Searchbar,
    Text,
    useTheme,
} from 'react-native-paper'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useConsumeNewlyCreated } from '@/lib/newlyCreatedRecord'
import { useStore } from '@/lib/pocketbase'
import type { Tag } from '@/types/tags'
import { Button } from '@/ui/Button'
import { PickerDialog } from '@/ui/PickerDialog'

interface TagSelectProps<TFieldValues extends FieldValues> {
    control: Control<TFieldValues>
    name: FieldPath<TFieldValues>
    label: string
    errorMessage?: string
}

interface TagDialogProps {
    visible: boolean
    onDismiss: () => void
    tags: Tag[]
    selectedIds: string[]
    onToggle: (id: string) => void
    onAddNew: () => void
}

function TagDialog({ visible, onDismiss, tags, selectedIds, onToggle, onAddNew }: TagDialogProps) {
    const theme = useTheme()
    const [searchQuery, setSearchQuery] = useState('')

    const filteredTags = searchQuery
        ? tags.filter(tag => tag.name.toLowerCase().includes(searchQuery.toLowerCase()))
        : tags

    const showSearchBox = tags.length > 5

    const handleDismiss = useCallback(() => {
        setSearchQuery('')
        onDismiss()
    }, [onDismiss])

    const handleAddNew = () => {
        setSearchQuery('')
        onAddNew()
    }

    return (
        <PickerDialog visible={visible} onDismiss={handleDismiss}>
            <Dialog.Title style={{ color: theme.colors.onSurface }}>Select Tags</Dialog.Title>
            <Dialog.Content>
                {showSearchBox && (
                    <Searchbar
                        placeholder="Search"
                        value={searchQuery}
                        onChangeText={setSearchQuery}
                        style={[styles.searchBar, { backgroundColor: theme.colors.surfaceVariant }]}
                    />
                )}
                <FlatList
                    data={filteredTags}
                    keyExtractor={tag => tag.id}
                    style={styles.list}
                    renderItem={({ item: tag }) => {
                        const isSelected = selectedIds.includes(tag.id)
                        const tagColor =
                            tag.color && tag.color.trim().length > 0 ? tag.color : '#6B7280'
                        return (
                            <TouchableOpacity style={styles.item} onPress={() => onToggle(tag.id)}>
                                <View style={styles.checkboxWrapper}>
                                    <Checkbox.Android
                                        status={isSelected ? 'checked' : 'unchecked'}
                                        onPress={() => onToggle(tag.id)}
                                    />
                                </View>
                                <View style={[styles.colorSwatch, { backgroundColor: tagColor }]} />
                                <Text
                                    variant="bodyLarge"
                                    style={[styles.itemText, { color: theme.colors.onSurface }]}
                                >
                                    {tag.name}
                                </Text>
                            </TouchableOpacity>
                        )
                    }}
                    ListEmptyComponent={
                        <Text
                            variant="bodyMedium"
                            style={{
                                color: theme.colors.onSurfaceVariant,
                                textAlign: 'center',
                                paddingVertical: 16,
                            }}
                        >
                            {searchQuery ? 'No tags found' : 'No tags yet'}
                        </Text>
                    }
                />
            </Dialog.Content>
            <Dialog.Actions style={styles.actions}>
                <Button title="Done" onPress={handleDismiss} />
                <Button title="Add New" mode="contained" icon="plus" onPress={handleAddNew} />
            </Dialog.Actions>
        </PickerDialog>
    )
}

export function TagSelect<TFieldValues extends FieldValues>({
    control,
    name,
    label,
    errorMessage,
}: TagSelectProps<TFieldValues>) {
    const { org: currentOrg } = useCurrentOrg()
    const [tagsCollection] = useStore('tags')
    const theme = useTheme()
    const router = useRouter()
    const pathname = usePathname()
    const [dialogVisible, setDialogVisible] = useState(false)

    const { isLoading, data: tags = [] } = useLiveQuery(
        q =>
            q
                .from({ tags: tagsCollection })
                .where(({ tags }) => eq(tags.org, currentOrg.id))
                .orderBy(({ tags }) => tags.name, 'asc'),
        [currentOrg.id]
    )

    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
    })

    const selectedIds: string[] = Array.isArray(field.value)
        ? field.value.filter((id: unknown): id is string => typeof id === 'string')
        : ([] as string[])

    const displayValue = selectedIds
        .map(id => tags.find((t: Tag) => t.id === id)?.name)
        .filter(Boolean)
        .join(', ')

    const effectiveError = errorMessage || error?.message

    const handleToggle = (id: string) => {
        const next = selectedIds.includes(id)
            ? selectedIds.filter(sid => sid !== id)
            : [...selectedIds, id]
        field.onChange(next)
    }

    const handleAddNew = () => {
        setDialogVisible(false)
        const returnTo = encodeURIComponent(pathname)
        router.push(`/${currentOrg.slug}/settings/tags/new?returnTo=${returnTo}`)
    }

    useConsumeNewlyCreated('tags', id => {
        const current = Array.isArray(field.value)
            ? field.value.filter((v: unknown): v is string => typeof v === 'string')
            : []
        if (!current.includes(id)) {
            field.onChange([...current, id])
        }
    })

    return (
        <View style={styles.container}>
            {isLoading ? (
                <View style={styles.loadingContainer}>
                    <Text variant="bodyMedium">{label}</Text>
                    <ActivityIndicator size="small" />
                </View>
            ) : (
                <>
                    <Text
                        variant="labelMedium"
                        style={[styles.label, { color: theme.colors.onSurfaceVariant }]}
                    >
                        {label}
                    </Text>
                    <TouchableOpacity
                        testID="tag-select"
                        onPress={() => setDialogVisible(true)}
                        style={[
                            styles.picker,
                            {
                                backgroundColor: theme.colors.surface,
                                borderColor: effectiveError
                                    ? theme.colors.error
                                    : theme.colors.outline,
                            },
                        ]}
                    >
                        <Text
                            variant="bodyLarge"
                            style={
                                displayValue
                                    ? { color: theme.colors.onSurface, flex: 1 }
                                    : { color: theme.colors.onSurfaceVariant, flex: 1 }
                            }
                            numberOfLines={1}
                        >
                            {displayValue || 'Select tags'}
                        </Text>
                        <Icon
                            source="chevron-down"
                            size={24}
                            color={theme.colors.onSurfaceVariant}
                        />
                    </TouchableOpacity>
                    {effectiveError ? (
                        <HelperText type="error" visible>
                            {effectiveError}
                        </HelperText>
                    ) : null}

                    <TagDialog
                        visible={dialogVisible}
                        onDismiss={() => setDialogVisible(false)}
                        tags={tags}
                        selectedIds={selectedIds}
                        onToggle={handleToggle}
                        onAddNew={handleAddNew}
                    />
                </>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        marginBottom: 16,
    },
    loadingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    label: {
        marginBottom: 4,
    },
    picker: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderWidth: 1,
        borderRadius: 4,
        padding: 12,
        minHeight: 56,
    },
    searchBar: {
        marginBottom: 12,
    },
    list: {
        maxHeight: 300,
    },
    item: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 2,
    },
    checkboxWrapper: {
        width: 36,
        alignItems: 'center',
        justifyContent: 'center',
    },
    colorSwatch: {
        width: 14,
        height: 14,
        borderRadius: 7,
        marginRight: 10,
    },
    itemText: {
        flex: 1,
    },
    actions: {
        justifyContent: 'space-between',
        paddingHorizontal: 16,
    },
})
